import React, { useState, useEffect } from 'react';
import { Modal, Button, FormInput, FormSelect } from '../../../components/ui';

const ROLE_OPTIONS = [
  { label: 'Select Role...', value: '' },
  { label: 'Carpenter', value: 'Carpenter' },
  { label: 'Plumber', value: 'Plumber' },
  { label: 'Electrician', value: 'Electrician' },
  { label: 'Painter', value: 'Painter' },
  { label: 'Other', value: 'Other' }
];

const STATE_OPTIONS = [
  { label: '-- Select State --', value: '' },
  { label: 'Andhra Pradesh', value: 'Andhra Pradesh' },
  { label: 'Telangana', value: 'Telangana' },
  { label: 'Karnataka', value: 'Karnataka' },
  { label: 'Tamil Nadu', value: 'Tamil Nadu' },
  { label: 'Bihar', value: 'Bihar' },
  { label: 'Other', value: 'Other' }
];

export default function AddInstallationMemberModal({ isOpen, onClose, onSave, initialData, isViewOnly }) {
  const [formData, setFormData] = useState({
    roleSelect: '',
    roleCustom: '',
    name: '',
    phone: '',
    lane1: '',
    lane2: '',
    city: '',
    district: '',
    stateSelect: '',
    stateCustom: '',
    pincode: '',
    status: 'Active'
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      const initialRole = initialData.role || '';
      const isPredefinedRole = ROLE_OPTIONS.some(opt => opt.value === initialRole && initialRole !== 'Other');
      const initialState = initialData.address?.state || '';
      const isPredefinedState = STATE_OPTIONS.some(opt => opt.value === initialState && initialState !== 'Other');
      
      setFormData({
        roleSelect: isPredefinedRole ? initialRole : (initialRole ? 'Other' : ''),
        roleCustom: isPredefinedRole ? '' : initialRole,
        name: initialData.name || '',
        phone: initialData.phone || '',
        lane1: initialData.address?.lane1 || '',
        lane2: initialData.address?.lane2 || '',
        city: initialData.address?.city || '',
        district: initialData.address?.district || '',
        stateSelect: isPredefinedState ? initialState : (initialState ? 'Other' : ''),
        stateCustom: isPredefinedState ? '' : initialState,
        pincode: initialData.address?.pincode || '',
        status: initialData.status || 'Active'
      });
    } else {
      setFormData({
        roleSelect: '',
        roleCustom: '',
        name: '',
        phone: '',
        lane1: '',
        lane2: '',
        city: '',
        district: '',
        stateSelect: '',
        stateCustom: '',
        pincode: '',
        status: 'Active'
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};
    const finalRole = formData.roleSelect === 'Other' ? formData.roleCustom.trim() : formData.roleSelect.trim();
    const finalState = formData.stateSelect === 'Other' ? formData.stateCustom.trim() : formData.stateSelect.trim();
    
    if (!finalRole) nextErrors.role = 'Role is required';
    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
    } else if (/[^a-zA-Z\s.-]/.test(formData.name.trim())) {
      nextErrors.name = 'Name must contain only letters';
    }
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!formData.phone.trim() || !phoneDigits) {
      nextErrors.phone = 'Phone number is required';
    } else if (phoneDigits.length !== 10) {
      nextErrors.phone = 'Phone number must be exactly 10 digits';
    }

    /* Developer : Nithin - Date : 21-07-2026 - Made Address Details fields (Address Line 1, City, State, PIN Code) optional */
    const pinDigits = formData.pincode.replace(/\D/g, '');
    if (formData.pincode.trim() && pinDigits.length !== 6) {
      nextErrors.pincode = 'PIN code must be exactly 6 digits if provided';
    }

    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewOnly) {
      onClose();
      return;
    }

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);
    try {
      const finalRole = formData.roleSelect === 'Other' ? formData.roleCustom.trim() : formData.roleSelect.trim();
      const finalState = formData.stateSelect === 'Other' ? formData.stateCustom.trim() : formData.stateSelect.trim();
      const payload = {
        role: finalRole,
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        address: {
          lane1: formData.lane1.trim(),
          lane2: formData.lane2.trim(),
          city: formData.city.trim(),
          district: formData.district.trim(),
          state: finalState,
          pincode: formData.pincode.trim()
        },
        status: formData.status
      };
      await onSave(payload);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isViewOnly ? "VIEW INSTALLATION MEMBER" : initialData ? "EDIT INSTALLATION MEMBER" : "ADD INSTALLATION MEMBER"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="flex flex-col space-y-3">
        {/* Basic Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <FormSelect
            label="Role *"
            name="roleSelect"
            value={formData.roleSelect}
            onChange={handleChange}
            options={ROLE_OPTIONS}
            disabled={isViewOnly}
            error={errors.role && !formData.roleCustom ? errors.role : null}
            size="sm"
          />
          {formData.roleSelect === 'Other' && (
            <FormInput
              label="Specify Role *"
              name="roleCustom"
              value={formData.roleCustom}
              onChange={handleChange}
              placeholder="e.g. Tile Contractor"
              disabled={isViewOnly}
              error={errors.role ? errors.role : null}
              size="sm"
            />
          )}
          <FormInput
            label="Member Name *"
            name="name"
            value={formData.name}
            onChange={(e) => {
              const val = e.target.value.replace(/[^a-zA-Z\s.-]/g, '');
              setFormData(prev => ({ ...prev, name: val }));
            }}
            placeholder="e.g. Ramesh Kumar"
            disabled={isViewOnly}
            error={errors.name}
            size="sm"
          />
          <FormInput
            label="Phone Number *"
            name="phone"
            type="tel"
            inputMode="numeric"
            value={formData.phone}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 10);
              setFormData(prev => ({ ...prev, phone: val }));
            }}
            placeholder="10-digit mobile number"
            disabled={isViewOnly}
            error={errors.phone}
            size="sm"
          />
          <FormSelect
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={[
              { label: 'Active', value: 'Active' },
              { label: 'Inactive', value: 'Inactive' }
            ]}
            disabled={isViewOnly}
            size="sm"
          />
        </div>

        {/* Address Details Section - Compact 2-column Layout exactly as requested */}
        <div className="pt-2 border-t border-slate-100">
          <h4 className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">Address Details</h4>
          <div className="space-y-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FormInput
                label="Address Line 1"
                name="lane1"
                value={formData.lane1}
                onChange={handleChange}
                placeholder="Street address, PO Box, etc."
                disabled={isViewOnly}
                error={errors.lane1}
                size="sm"
              />
              <FormInput
                label="Address Line 2"
                name="lane2"
                value={formData.lane2}
                onChange={handleChange}
                placeholder="Apartment, suite, unit, etc."
                disabled={isViewOnly}
                size="sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FormInput
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                disabled={isViewOnly}
                error={errors.city}
                size="sm"
              />
              <FormInput
                label="District"
                name="district"
                value={formData.district}
                onChange={handleChange}
                placeholder="District"
                disabled={isViewOnly}
                size="sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FormSelect
                label="State"
                name="stateSelect"
                value={formData.stateSelect}
                onChange={handleChange}
                options={STATE_OPTIONS}
                disabled={isViewOnly}
                error={errors.state && !formData.stateCustom ? errors.state : null}
                size="sm"
              />
              {formData.stateSelect === 'Other' ? (
                <FormInput
                  label="Specify State"
                  name="stateCustom"
                  value={formData.stateCustom}
                  onChange={handleChange}
                  placeholder="e.g. Maharashtra"
                  disabled={isViewOnly}
                  error={errors.state ? errors.state : null}
                  size="sm"
                />
              ) : (
                <FormInput
                  label="PIN Code"
                  name="pincode"
                  type="tel"
                  inputMode="numeric"
                  value={formData.pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setFormData(prev => ({ ...prev, pincode: val }));
                  }}
                  placeholder="6-digit PIN"
                  disabled={isViewOnly}
                  error={errors.pincode}
                  size="sm"
                />
              )}
            </div>
            {formData.stateSelect === 'Other' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <FormInput
                  label="PIN Code"
                  name="pincode"
                  type="tel"
                  inputMode="numeric"
                  value={formData.pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setFormData(prev => ({ ...prev, pincode: val }));
                  }}
                  placeholder="6-digit PIN"
                  disabled={isViewOnly}
                  error={errors.pincode}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100 mt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {isViewOnly ? "Close" : "Cancel"}
          </Button>
          {!isViewOnly && (
            <Button type="submit" variant="primary" size="sm" loading={loading}>
              {initialData ? "Save Changes" : "Create Member"}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}




